/**
 * 根据点位名、监测项目推断 basic_type / sub_type / type_code（与 config/routine_type_infer.json 对齐）
 */

function matchAny(text, patterns) {
  if (!text || !patterns || !patterns.length) return false
  const upper = String(text).toUpperCase()
  return patterns.some(p => {
    if (!p) return false
    return upper.includes(String(p).toUpperCase()) || String(text).includes(p)
  })
}

function ruleMatchesSite(rule, site) {
  return matchAny(site, rule.patterns)
}

function ruleMatchesItem(rule, site, items) {
  const hay = `${site || ''} ${items || ''}`.trim()
  if (!matchAny(hay, rule.patterns)) return false
  if (matchAny(site, rule.exclude_site_patterns)) return false
  const req = rule.require_site_patterns
  if (req && req.length && !matchAny(site, req)) return false
  return true
}

export function inferMonitorType(siteName, itemNames, inferCfg, typeConfig) {
  const site = String(siteName || '').trim()
  const items = String(itemNames || '').trim()
  const cfg = inferCfg || {}
  let best = null
  let bestPrio = -1

  for (const rule of cfg.site_rules || []) {
    if (!ruleMatchesSite(rule, site)) continue
    const prio = Number(rule.priority) || 0
    if (prio > bestPrio) {
      best = rule
      bestPrio = prio
    }
  }
  if (!best) {
    for (const rule of cfg.item_rules || []) {
      if (!ruleMatchesItem(rule, site, items)) continue
      const prio = Number(rule.priority) || 0
      if (prio > bestPrio) {
        best = rule
        bestPrio = prio
      }
    }
  }
  if (!best || !best.basic_type) return {}

  const subTypeCode = lookupTypeCode(best.basic_type, best.sub_type, typeConfig)
  return {
    basic_type: best.basic_type,
    sub_type: best.sub_type,
    type_code: subTypeCode
  }
}

export function lookupTypeCode(basicType, subType, typeConfig) {
  const basics = (typeConfig && typeConfig.basicTypes) || []
  const b = basics.find(x => x.label === basicType)
  if (!b) return ''
  const s = (b.subTypes || []).find(x => x.label === subType)
  return s ? (s.code || '') : ''
}

/** 旧版 basic_type 映射到 11 类检测项目（保留可识别的子类型） */
export function normalizeLegacyBasicType(basicType, subType) {
  const bt = String(basicType || '').trim()
  const st = String(subType || '').trim()
  if (!bt) return { basic_type: '', sub_type: st }
  if (bt === '水（含大气降水）和废水') {
    return { basic_type: '水质检测', sub_type: st || '废水' }
  }
  if (bt === '气和废气') {
    if (st === '无组织废气' || st === '环境空气') {
      return { basic_type: '环境空气（无组织废气）', sub_type: st || '无组织废气' }
    }
    return { basic_type: '烟气检测', sub_type: st || '有组织废气' }
  }
  if (bt === '土壤和沉积物') {
    if (st === '沉积物') return { basic_type: '沉积物采样', sub_type: '沉积物' }
    return { basic_type: '土壤采样', sub_type: st || '土壤' }
  }
  if (bt === '噪声') {
    const subMap = { 厂界噪声: '工业企业环境噪声', 区域环境噪声: '工业企业环境噪声' }
    return { basic_type: '噪声检测', sub_type: subMap[st] || st || '工业企业环境噪声' }
  }
  return { basic_type: bt, sub_type: st }
}

/** 配置未下发子类型时的本地默认（与 entrust_monitor_types.json 对齐） */
const DEFAULT_SUB_BY_BASIC = {
  土壤采样: { label: '土壤', code: 'TR' },
  烟气检测: { label: '有组织废气', code: 'YZ' },
  烟气比对检测: { label: 'CEMS比对', code: 'BD' },
  水质检测: { label: '废水', code: 'FS' },
  '环境空气（无组织废气）': { label: '无组织废气', code: 'WZZ' },
  固体废物: { label: '固体废物', code: 'GF' },
  '在线检测（加油站油气回收）': { label: '油气回收', code: 'YQHS' },
  噪声检测: { label: '工业企业环境噪声', code: 'GY' },
  '工频电场、磁场检测': { label: '工频电磁场', code: 'GP' },
  沉积物采样: { label: '沉积物', code: 'CJ' },
  泄露和敞开液面排放: { label: 'LDAR', code: 'LDAR' }
}

/** 无子类型时按配置补默认（仅 1 个子类型则自动选中；否则用本地默认） */
export function ensureDefaultSubType(form, typeConfig) {
  if (!form || form.sub_type) return form
  const basics = (typeConfig && typeConfig.basicTypes) || []
  const b = basics.find(x => x.label === form.basic_type)
  const subs = (b && b.subTypes) || []
  if (subs.length === 1) {
    form.sub_type = subs[0].label || ''
    if (!form.type_code) form.type_code = subs[0].code || ''
    return form
  }
  const fallback = DEFAULT_SUB_BY_BASIC[form.basic_type]
  if (fallback) {
    form.sub_type = fallback.label
    if (!form.type_code) form.type_code = fallback.code
  }
  return form
}

export function applyInferredTypes(form, site, inferCfg, typeConfig) {
  if (form.basic_type && form.sub_type) return form
  const inferred = inferMonitorType(
    site.site_name || form.monitor_place,
    site.item_names || form.item_names,
    inferCfg,
    typeConfig
  )
  if (!inferred.basic_type) {
    ensureDefaultSubType(form, typeConfig)
    return form
  }
  if (!form.basic_type) form.basic_type = inferred.basic_type
  if (!form.sub_type) form.sub_type = inferred.sub_type
  if (!form.type_code) form.type_code = inferred.type_code
  ensureDefaultSubType(form, typeConfig)
  return form
}
